import React, { useEffect, useMemo, useState } from 'react';
import api from '../utils/api';
import { useFiscalYear } from '../contexts/FiscalYearContext';

/**
 * Other Purchases — assets, office expenses, utilities, and other
 * purchases that don't have a Purchase Order in the procurement workflow.
 * These records still need to be reported on the Bahrain VAT return —
 * most fall under line 13 by default.
 */

const VAT_CATEGORY_OPTIONS = [
  { value: 'non_registered_zero_rated', label: 'Line 13 — Purchases from non-registered taxpayers, zero-rated/exempt' },
  { value: 'standard_rated_domestic',  label: 'Line 8 — Standard rated domestic purchases' },
  { value: 'import_vat_at_customs',    label: 'Line 9 — Imports subject to VAT paid at customs' },
  { value: 'import_vat_deferral',      label: 'Line 10 — Imports subject to deferral at customs' },
  { value: 'import_reverse_charge',    label: 'Line 11 — Imports subject to VAT (reverse charge 10%)' },
  { value: 'purchase_reverse_charge',  label: 'Line 12 — Purchases subject to domestic reverse charge' },
];

const CATEGORY_OPTIONS = ['Asset', 'Office Expense', 'Utility', 'Travel', 'Subscription', 'Other'];

const emptyForm = () => ({
  purchase_date: new Date().toISOString().slice(0, 10),
  description: '',
  supplier_name: '',
  category: 'Office Expense',
  amount_bhd: '',
  vat_amount: '',
  vat_category: 'non_registered_zero_rated',
  invoice_reference: '',
  notes: '',
});

export default function OtherPurchases() {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ total_amount_bhd: 0, total_vat_bhd: 0, count: 0 });
  const [filters, setFilters] = useState({ start_date: '', end_date: '' });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const { selectedFy } = useFiscalYear();

  // Sync global Fiscal Year selection → server-side filter.
  useEffect(() => {
    if (!selectedFy) return;
    setFilters({ start_date: selectedFy.start_date, end_date: selectedFy.end_date });
  }, [selectedFy?.id]);

  const load = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      const res = await api.get('/other-purchases', { params });
      setItems(res.data.items || []);
      setTotals({
        total_amount_bhd: res.data.total_amount_bhd || 0,
        total_vat_bhd: res.data.total_vat_bhd || 0,
        count: res.data.count || 0,
      });
      setErr('');
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* mount + when filters change */ }, [filters.start_date, filters.end_date]); // eslint-disable-line

  const openNew = () => { setEditId(null); setForm(emptyForm()); setShowForm(true); };
  const openEdit = (it) => {
    setEditId(it.id);
    setForm({
      purchase_date: it.purchase_date || '',
      description: it.description || '',
      supplier_name: it.supplier_name || '',
      category: it.category || 'Other',
      amount_bhd: it.amount_bhd ?? '',
      vat_amount: it.vat_amount ?? '',
      vat_category: it.vat_category || 'non_registered_zero_rated',
      invoice_reference: it.invoice_reference || '',
      notes: it.notes || '',
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.purchase_date || !form.description || !form.amount_bhd) {
      alert('Date, Description and Amount are required.');
      return;
    }
    const payload = {
      ...form,
      amount_bhd: Number(form.amount_bhd || 0),
      vat_amount: Number(form.vat_amount || 0),
    };
    try {
      if (editId) {
        await api.put(`/other-purchases/${editId}`, payload);
      } else {
        await api.post('/other-purchases', payload);
      }
      setShowForm(false);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await api.delete(`/other-purchases/${id}`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString(undefined,
    { minimumFractionDigits: 3, maximumFractionDigits: 3 });

  // Derived: rough preview of which NBR line each entry will land on.
  const categorySummary = useMemo(() => {
    const byCat = {};
    for (const it of items) {
      const c = it.vat_category || 'non_registered_zero_rated';
      byCat[c] = byCat[c] || { net: 0, vat: 0, count: 0 };
      byCat[c].net += Number(it.amount_bhd || 0);
      byCat[c].vat += Number(it.vat_amount || 0);
      byCat[c].count += 1;
    }
    return byCat;
  }, [items]);

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="other-purchases-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">📂 Other Purchases</h1>
          <p className="text-sm text-gray-600 mt-1">
            Capture purchases without a PO — assets, office expenses, utilities, travel, etc.
            Flow into the VAT Return (default: line 13).
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={openNew}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2 rounded-lg shadow"
                  data-testid="other-purchase-new-btn">+ New Entry</button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-lg p-3 grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">From</label>
          <input type="date" value={filters.start_date}
                 onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                 className="w-full border rounded px-2 py-1.5 text-sm" data-testid="op-from"/>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">To</label>
          <input type="date" value={filters.end_date}
                 onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                 className="w-full border rounded px-2 py-1.5 text-sm" data-testid="op-to"/>
        </div>
        <div className="md:col-span-2 text-sm text-gray-700 bg-emerald-50 border border-emerald-200 rounded p-2">
          <b>{totals.count}</b> entries · Net <b>{fmt(totals.total_amount_bhd)}</b> BHD ·
          VAT <b>{fmt(totals.total_vat_bhd)}</b> BHD
        </div>
      </div>

      {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-2">{err}</div>}

      {/* List */}
      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-left">
            <tr>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Description</th>
              <th className="px-3 py-2">Supplier</th>
              <th className="px-3 py-2">Category</th>
              <th className="px-3 py-2">VAT Line</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-right">VAT</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-400 italic">
                No entries in this period. Click "+ New Entry" to add one.
              </td></tr>
            )}
            {items.map((it) => {
              const lineOpt = VAT_CATEGORY_OPTIONS.find((o) => o.value === it.vat_category);
              return (
                <tr key={it.id} className="border-t hover:bg-gray-50">
                  <td className="px-3 py-2 whitespace-nowrap">{it.purchase_date}</td>
                  <td className="px-3 py-2">{it.description}</td>
                  <td className="px-3 py-2 text-gray-700">{it.supplier_name || '-'}</td>
                  <td className="px-3 py-2"><span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{it.category}</span></td>
                  <td className="px-3 py-2 text-xs text-gray-600">{lineOpt ? lineOpt.label.split('—')[0] : 'Line 13'}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(it.amount_bhd)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(it.vat_amount)}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => openEdit(it)} className="text-blue-600 hover:underline text-xs mr-2">Edit</button>
                    <button onClick={() => remove(it.id)} className="text-red-600 hover:underline text-xs">Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Category summary preview */}
      {Object.keys(categorySummary).length > 0 && (
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs">
          <div className="font-semibold text-blue-900 mb-1">Will appear on VAT Return as:</div>
          <ul className="space-y-1">
            {Object.entries(categorySummary).map(([cat, v]) => {
              const lineOpt = VAT_CATEGORY_OPTIONS.find((o) => o.value === cat);
              return (
                <li key={cat} className="text-blue-800">
                  • <b>{lineOpt ? lineOpt.label : cat}</b> — net {fmt(v.net)} BHD, VAT {fmt(v.vat)} BHD ({v.count} entries)
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="op-modal">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b bg-emerald-50">
              <h3 className="text-lg font-bold text-gray-900">{editId ? '✏ Edit' : '+ New'} Other Purchase</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-700 text-xl">×</button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1">Date *</label>
                  <input type="date" value={form.purchase_date}
                         onChange={(e) => setForm({...form, purchase_date: e.target.value})}
                         className="w-full border rounded px-2 py-1.5"/>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1">Category</label>
                  <select value={form.category}
                          onChange={(e) => setForm({...form, category: e.target.value})}
                          className="w-full border rounded px-2 py-1.5">
                    {CATEGORY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Description *</label>
                <input type="text" value={form.description}
                       onChange={(e) => setForm({...form, description: e.target.value})}
                       placeholder="e.g. Office laptop, monthly electricity bill, …"
                       className="w-full border rounded px-2 py-1.5"/>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1">Supplier name</label>
                  <input type="text" value={form.supplier_name}
                         onChange={(e) => setForm({...form, supplier_name: e.target.value})}
                         placeholder="Free text — no need to create supplier master"
                         className="w-full border rounded px-2 py-1.5"/>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1">Invoice / Receipt #</label>
                  <input type="text" value={form.invoice_reference}
                         onChange={(e) => setForm({...form, invoice_reference: e.target.value})}
                         className="w-full border rounded px-2 py-1.5"/>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1">Amount (BHD, net) *</label>
                  <input type="number" step="0.001" value={form.amount_bhd}
                         onChange={(e) => setForm({...form, amount_bhd: e.target.value})}
                         className="w-full border rounded px-2 py-1.5"/>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1">VAT (BHD)</label>
                  <input type="number" step="0.001" value={form.vat_amount}
                         onChange={(e) => setForm({...form, vat_amount: e.target.value})}
                         className="w-full border rounded px-2 py-1.5"/>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">VAT Return Line (NBR category)</label>
                <select value={form.vat_category}
                        onChange={(e) => setForm({...form, vat_category: e.target.value})}
                        className="w-full border rounded px-2 py-1.5">
                  {VAT_CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <p className="text-xs text-gray-500 mt-1">Default is line 13 — change if this purchase falls under a different VAT category.</p>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Notes</label>
                <textarea value={form.notes}
                          onChange={(e) => setForm({...form, notes: e.target.value})}
                          rows={2}
                          className="w-full border rounded px-2 py-1.5"/>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t bg-gray-50">
              <button onClick={() => setShowForm(false)} className="px-3 py-1.5 rounded border bg-white hover:bg-gray-100 text-sm">Cancel</button>
              <button onClick={save} className="px-4 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm" data-testid="op-save-btn">
                {editId ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
